import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getAlbums, getSongs } from "../../lib/api";
import { Play } from "lucide-react";
import SEO from "../../components/SEO";

export default function Catalog() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const [albums, setAlbums] = useState([]);
  const [songs, setSongs] = useState([]);
  const [activeAlbum, setActiveAlbum] = useState("vol1");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const a = await getAlbums();
        setAlbums(a.albums || []);
      } catch (e) { console.error(e); }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const s = await getSongs(activeAlbum);
        setSongs(s);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [activeAlbum]);

  return (
    <div data-testid="anthem-catalog-page">
      <SEO
        path="/anthem/catalog"
        title="The Catalog | Foster Care Anthem · VOL I + Anthem Editions"
        description="Three releases. Thirty-plus tracks. The Foster Care Anthem catalog: VOL I (15 cornerstone tracks), VOL I · Anthem Editions (campaign-ready alternates), and the trafficking-awareness single 'We're Coming For You.'"
      />
      <section className="border-b border-an-border">
        <div className="container-xl pt-24 pb-16">
          <div className="overline text-an-gold mb-5">The Catalog · A Legacy Project Hub</div>
          <h1 className="font-display text-6xl md:text-8xl leading-[0.9]">Every record. <br/><span className="italic">Every reckoning.</span></h1>
          <p className="mt-6 text-an-muted text-lg max-w-3xl">
            Three releases. Thirty-plus tracks. One permanent mission — 24/7 advocacy, awareness, and storytelling for foster care.
          </p>
        </div>
      </section>

      <section className="border-b border-an-border">
        <div className="container-xl py-20">
          <div className="overline text-an-muted mb-8">The Discography · Three Releases</div>
          <div className="grid md:grid-cols-3 gap-6">
            {albums.map((a) => (
              <button key={a.slug} onClick={() => setActiveAlbum(a.slug)}
                className={`text-left border ${activeAlbum===a.slug?"border-an-gold":"border-an-border"} bg-an-surface p-5 card-hover`}
                data-testid={`catalog-album-tab-${a.slug}`}>
                <img src={a.cover} alt={a.title} className="w-full aspect-square object-cover" />
                <div className="overline mt-4 text-an-gold">{a.status}</div>
                <div className="font-display text-xl mt-1">{a.title}</div>
                <div className="text-sm text-an-muted">{a.subtitle}</div>
              </button>
            ))}
          </div>
        </div>
      </section>

      <section id="tracklist" className="border-b border-an-border">
        <div className="container-xl py-20">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
            <h2 className="font-display text-4xl md:text-5xl">Tracklist</h2>
            <div className="flex gap-3 flex-wrap">
              {albums.map((a) => (
                <button key={a.slug} onClick={() => setActiveAlbum(a.slug)}
                  className={`overline border px-4 py-2 ${activeAlbum===a.slug?"border-an-gold text-an-gold":"border-an-border text-an-muted hover:border-an-ink hover:text-an-ink"}`}
                  data-testid={`catalog-filter-${a.slug}`}>
                  {a.title}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="font-mono text-an-muted py-10">Loading…</div>
          ) : (
            <div>
              <div className="hidden md:grid grid-cols-[60px,1fr,240px,120px,120px,80px] gap-4 overline text-an-muted pb-3 border-b border-an-border">
                <div>№</div><div>Title</div><div>Tagline</div><div>BPM</div><div>Key</div><div className="text-right">Listen</div>
              </div>
              <div>
                {songs.map((s) => (
                  <Link key={s.slug} to={`/anthem/song/${s.slug}`} className="tracklist-row grid md:grid-cols-[60px,1fr,240px,120px,120px,80px] grid-cols-[40px,1fr,80px] gap-4 py-5 px-2 items-center" data-testid={`tracklist-row-${s.slug}`}>
                    <div className="font-mono text-an-muted text-sm">{String(s.number).padStart(2,"0")}</div>
                    <div className="flex items-center gap-4">
                      {s.image && <img src={s.image} alt="" className="w-12 h-12 object-cover hidden md:block" />}
                      <div>
                        <div className="font-display text-lg md:text-xl leading-tight">{s.title}</div>
                        <div className="overline text-an-muted md:hidden">{s.mood}</div>
                      </div>
                    </div>
                    <div className="text-sm text-an-muted italic hidden md:block">"{s.tagline}"</div>
                    <div className="font-mono text-sm text-an-muted hidden md:block">{s.bpm || "—"}</div>
                    <div className="font-mono text-sm text-an-muted hidden md:block">{s.key || "—"}</div>
                    <div className="flex justify-end">
                      <Play size={18} className="text-an-muted group-hover:text-an-gold" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Browse by Volume */}
      <section>
        <div className="container-xl py-20">
          <h2 className="font-display text-3xl md:text-4xl mb-10">Browse by Volume</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {albums.map((a) => (
              <div key={a.slug} className="border border-an-border bg-an-surface p-5">
                <img src={a.cover} alt={a.title} className="w-full aspect-square object-cover" />
                <div className="overline mt-4 text-an-gold">{a.status}</div>
                <div className="font-display text-lg mt-1">{a.title}</div>
                <button onClick={() => { setActiveAlbum(a.slug); const el=document.getElementById("tracklist"); el&&el.scrollIntoView({behavior:"smooth"}); }} className="mt-4 overline link-ul" data-testid={`browse-volume-${a.slug}`}>Open Volume →</button>
              </div>
            ))}
            <div className="border border-dashed border-an-border p-5 flex flex-col justify-center">
              <div className="overline text-an-muted mb-2">Vol. II</div>
              <div className="font-display text-lg">Foster Care Anthem VOL2</div>
              <div className="text-sm text-an-muted mt-1">Next chapter · Coming Soon</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
